/* eslint-disable jira/react/no-style-attribute, @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/enforce-style-prop */
import React, { useEffect, useState } from 'react';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { useIsGlobalHydrationComplete } from '@jira/ssr__global-hydration-completeness/src/useIsGlobalHydrationComplete.tsx';
import { useIsStagingOrDevOrLocal } from '@atlassian/jira-tenant-context-controller/src/components/environment/index.tsx';
import { HydrationErrorBadge } from './HydrationErrorBadge.tsx';

// I promise I am being a good developer and not cheating the VC system. This component is strictly for development use
// only and cannot affect VC for customers.
const attrKey = 'vc-nvs';
const IGNORE_VC_ATTRIBUTE = {
	[`data-${attrKey}`]: true,
};

// Added to avoid type errors when checking __HYDRATION_RECOVERABLE_ERRORS__
declare global {
	interface Window {
		__HYDRATION_RECOVERABLE_ERRORS__?: unknown[];
	}
}

export const HydrationAdvisory = (): React.JSX.Element | null => {
	const [isAdvisoryVisible, setIsAdvisoryVisible] = useState(true);
	const [errorsWereDetected, setErrorsWereDetected] = useState(false);
	const isHydrateRenderMode = useIsHydrateRenderMode();
	const isGlobalHydrationComplete = useIsGlobalHydrationComplete();
	const isNonProd = useIsStagingOrDevOrLocal();

	useEffect(() => {
		const timer = setTimeout(() => {
			setIsAdvisoryVisible(false);
		}, 5000);

		return () => {
			clearTimeout(timer);
		};
	}, []);

	useEffect(() => {
		if (!isGlobalHydrationComplete) {
			return;
		}
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		if (window.__HYDRATION_RECOVERABLE_ERRORS__?.length) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setErrorsWereDetected(true);
		}
	}, [isGlobalHydrationComplete]);

	if (!isHydrateRenderMode || !isNonProd) {
		return null;
	}

	const dotColor = errorsWereDetected ? 'red' : 'aqua';

	return (
		<div
			{...IGNORE_VC_ATTRIBUTE}
			style={{
				position: 'absolute',
				left: 8,
				top: 12,
				display: 'flex',
				alignItems: 'center',
				// By giving this element a height of 0 and no pointer events, we ensure that it doesn't interfere with the page.
				// To make is visible again, we then set the overflow to "visible".
				height: 0,
				overflow: 'visible',
				pointerEvents: 'none',
				zIndex: 99999999,
			}}
		>
			<div
				style={{
					backgroundColor: dotColor,
					borderRadius: '50%',
					width: 8,
					height: 8,
					zIndex: 20,
				}}
			/>

			{isAdvisoryVisible && (
				<div
					style={{
						backgroundColor: 'midnightblue',
						color: 'aqua',
						height: '1.5em',
						marginLeft: -12,
						paddingLeft: 16,
						paddingRight: 8,
						borderRadius: 4,
						zIndex: 10,
						display: 'flex',
						alignItems: 'center',
						gap: 8,
					}}
				>
					<samp>Rendered with React hydration 🚰 go/jira-hydration</samp>
				</div>
			)}
			<HydrationErrorBadge />
		</div>
	);
};
